import { defineStore } from 'pinia'
import { getMyPreferences, updatePreferences } from '@/services/preferences'
import { useAuthStore } from '@/stores/auth'
import { getAccessToken } from '@/auth/storage'

const THEME_KEY = 'theme.preferences.v1'

const PALETTE_TOKENS = {
  indigo: { primary: '#4C5AD3', hover: '#2B3175', light: '#EEF0FF', dark: '#242A63' },
  brehan: { primary: '#2B3175', hover: '#242A63', light: '#EEF0FF', dark: '#1E2352' },
  // Primary and hover colors must retain at least 4.5:1 contrast with white
  // because legacy utility classes use them as button backgrounds.
  emerald: { primary: '#047857', hover: '#065F46', light: '#ECFDF5', dark: '#064E3B' },
  rose: { primary: '#BE123C', hover: '#9F1239', light: '#FFF1F2', dark: '#881337' },
  violet: { primary: '#6D28D9', hover: '#5B21B6', light: '#F5F3FF', dark: '#4C1D95' },
  amber: { primary: '#92400E', hover: '#78350F', light: '#FFFBEB', dark: '#713F12' },
  slate: { primary: '#475569', hover: '#334155', light: '#F8FAFC', dark: '#1E293B' },
}

export const useThemeStore = defineStore('theme', {
  state: () => ({
    mode: 'light', // 'light' | 'dark' | 'system'
    palette: 'brehan', // 'brehan' | 'indigo' | 'emerald' | 'rose' | 'violet' | 'amber' | 'slate'
    _media: null,
  }),
  actions: {
    apply() {
      const root = document.documentElement
      const current = (root.className || '').split(/\s+/).filter(Boolean)
      const filtered = current.filter(c => !c.startsWith('theme-') && c !== 'theme-animating')

      // Set palette via both class and data attribute for robustness
      const paletteClass = `theme-${this.palette}`
      root.setAttribute('data-theme', this.palette)

      const isDark = this.mode === 'dark' || (this.mode === 'system' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
      const next = new Set(filtered)
      if (isDark) next.add('dark'); else next.delete('dark')
      next.add(paletteClass)

      // Add lightweight animation helper class
      next.add('theme-animating')
      root.className = Array.from(next).join(' ')
      const tokens = PALETTE_TOKENS[this.palette] || PALETTE_TOKENS.indigo
      try {
        root.style.setProperty('--color-primary', tokens.primary)
        root.style.setProperty('--color-primary-hover', tokens.hover)
        root.style.setProperty('--color-primary-light', tokens.light)
        root.style.setProperty('--color-primary-dark', tokens.dark)
      } catch (_) {}
      // Mirror to body for components that rely on body selector
      try {
        document.body.classList.toggle('dark', isDark)
        // ensure only one theme-* on body
        const bodyClasses = new Set(Array.from(document.body.classList))
        for (const c of Array.from(bodyClasses)) { if (c.startsWith('theme-')) document.body.classList.remove(c) }
        document.body.classList.add(paletteClass)
      } catch (_) {}
      // Remove animating flag after a tick
      setTimeout(() => {
        const now = new Set((document.documentElement.className || '').split(/\s+/).filter(Boolean))
        now.delete('theme-animating')
        document.documentElement.className = Array.from(now).join(' ')
      }, 220)

      try { localStorage.setItem(THEME_KEY, JSON.stringify({ mode: this.mode, palette: this.palette })) } catch (_) {}
    },
    setMode(mode) {
      this.mode = mode
      this.apply()
      this.saveServer()
    },
    setPalette(palette) {
      this.palette = palette
      this.apply()
      this.saveServer()
    },
    init() {
      try {
        const saved = JSON.parse(localStorage.getItem(THEME_KEY) || 'null')
        if (saved && (saved.mode || saved.palette)) {
          if (saved.mode) this.mode = saved.mode
          if (saved.palette) this.palette = saved.palette
        }
      } catch (_) {}
      if (window.matchMedia) {
        this._media = window.matchMedia('(prefers-color-scheme: dark)')
        const listener = () => { if (this.mode === 'system') this.apply() }
        if (this._media.addEventListener) this._media.addEventListener('change', listener)
        else if (this._media.addListener) this._media.addListener(listener)
      }
      this.apply()
      this.syncFromServer()
    },
    async syncFromServer() {
      try {
        const auth = useAuthStore()
        const token = getAccessToken()
        if (!auth?.isAuthenticated || !token) return
        const prefs = await getMyPreferences()
        if (prefs && (prefs.theme_mode || prefs.theme_palette)) {
          if (prefs.theme_mode) this.mode = prefs.theme_mode
          if (prefs.theme_palette) this.palette = prefs.theme_palette
          this.apply()
        }
      } catch (_) {}
    },
    async saveServer() {
      try {
        const auth = useAuthStore()
        const token = getAccessToken()
        if (!auth?.isAuthenticated || !token) return
        await updatePreferences({ theme_mode: this.mode, theme_palette: this.palette })
      } catch (_) {}
    }
  },
  persist: true,
})
